import React from 'react';
import { ArrowRight, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

export const Hero = () => {
  return (
    <div className="relative bg-simba-cream overflow-hidden min-h-screen flex items-center pt-28 lg:pt-36">
      
      {/* Abstract Organic Shapes Background */}
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-simba-lime/20 rounded-full blur-[120px] mix-blend-multiply animate-pulse-slow"></div>
        <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-simba-gold/10 rounded-full blur-[100px] mix-blend-multiply"></div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 w-full relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          
          {/* Left: Typography Content */}
          <div className="lg:col-span-7 space-y-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center space-x-2 bg-simba-green/5 border border-simba-green/10 rounded-full px-4 py-1.5 backdrop-blur-sm"
            >
              <Star size={14} className="text-simba-gold fill-current" />
              <span className="text-simba-green font-body font-semibold text-xs uppercase tracking-widest">
                Simba Herbals UK
              </span>
            </motion.div>

            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="font-display font-medium text-5xl lg:text-[5rem] leading-[1.1] text-simba-green"
            >
              Pure Mufasa Energy: <br/>
              <span className="italic font-light text-simba-gold">Rule Your Jungle</span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="font-body text-simba-dark/70 text-lg lg:text-xl max-w-xl leading-relaxed"
            >
              Unleash the Beast. Reclaim Your Throne. Rule the Jungle. 100% African Natural Herbs for daily vitality and confidence.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-6 pt-4"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="z-20"
              >
                <Link 
                  to="/shop" 
                  className="group relative inline-flex items-center justify-center bg-simba-green text-white px-10 py-5 rounded-full font-body font-semibold tracking-wide overflow-hidden shadow-xl hover:shadow-2xl hover:shadow-simba-green/20 transition-all duration-300"
                >
                  <span className="relative z-10 flex items-center">
                    Shop The Protocol <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                  </span>
                  {/* Hover Background Layer */}
                  <div className="absolute inset-0 bg-simba-dark transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500 ease-out z-0"></div>
                </Link>
              </motion.div>
              
              <div className="flex items-center space-x-4">
                <div className="flex -space-x-3">
                  {[1,2,3].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full border-2 border-white bg-gray-200 overflow-hidden">
                      <img src={`https://randomuser.me/api/portraits/men/${30+i}.jpg`} alt="User" />
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex text-simba-gold">
                    {[1,2,3,4,5].map(i => <Star key={i} size={12} fill="currentColor" />)}
                  </div>
                  <p className="text-xs font-bold text-simba-green mt-0.5">1k+ Happy Clients</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right: Immersive Visuals */}
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="lg:col-span-5 relative hidden lg:block"
          >
            <div className="relative z-10 transform lg:translate-x-10 lg:translate-y-10 group cursor-pointer">
              <div className="absolute inset-0 bg-simba-gold/20 rounded-[2rem] transform rotate-6 scale-95 group-hover:rotate-3 transition-transform duration-700"></div>
              <img 
                src="/images/section/ayurvedic-health-tonic.jpg" 
                alt="Jungle Forest" 
                className="relative rounded-[2rem] shadow-2xl w-full h-[600px] object-cover hover:scale-[1.02] transition-transform duration-700 ease-in-out"
              />
              
              {/* Stats Badge */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.8 }}
                whileHover={{ y: -8, scale: 1.05 }}
                className="absolute bottom-10 -left-12 bg-white/90 backdrop-blur-md p-6 rounded-2xl shadow-xl border border-white/50 max-w-[200px]"
              >
                 <div className="text-center">
                    <span className="block text-3xl font-bold text-simba-green font-display">10+</span>
                    <span className="text-xs text-gray-500 uppercase tracking-widest">Years of Experience</span>
                 </div>
              </motion.div>
            </div>
          </motion.div>

        </div>
      </div>
    </div>
  );
};
